// matchmakingService.js
// Pick Up Draft — Phase B2 (Matchmaking).
//
// Web mirror of the matchmaking half of FirebaseRepository_PickUp.swift + the queue
// state machine in MatchmakingManager.swift. One independent queue per sport, keyed by
// "{userID}_{Sport}".
//
// CRITICAL (see plan §4a): re-queuing must DELETE any stale queue doc before writing the
// fresh one. Two reasons, both load-bearing:
//   1. matchPlayers triggers on document CREATE — overwriting an existing doc is an update
//      and would never pair you with anyone.
//   2. The security rules only allow an UPDATE when status == "cancelled"; a fresh
//      status: "waiting" write must therefore be a create.
//
// Match detection: matchPlayers writes status "matched" + matchedGameID onto BOTH queue
// entries. The per-sport doc listener sees that and hands the gameID back to the caller,
// who loads the game (decoded via pickupService.decodePickUpGame).
//
// The 3-hour expiresAt is client-computed for the countdown display only — the server
// (expireMatchmakingEntries, hourly) is the authority that flips stale entries to "expired".

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { decodePickUpGame } from "./pickupService";

// Sport id ↔ raw enum. Local to this service, matching the per-service convention used by
// knockoutService.js (each service owns its small sport map).
const RAW_FROM_ID = {
  football: "Football",
  baseball: "Baseball",
  basketball: "Basketball",
  hockey: "Hockey",
  soccer: "Soccer",
};
const ID_FROM_RAW = Object.fromEntries(
  Object.entries(RAW_FROM_ID).map(([id, raw]) => [raw, id])
);

const QUEUE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours (matches MatchmakingEntry init)

function queueDocID(userID, sportId) {
  return `${userID}_${RAW_FROM_ID[sportId]}`;
}

function toDate(ts) {
  return ts && typeof ts.toDate === "function" ? ts.toDate() : null;
}

// ─── Queue writes ─────────────────────────────────────────────────────────────

/**
 * Enters the matchmaking queue for one sport. Deletes any stale entry first so the write
 * is a create (see header). Returns { queuedAt, expiresAt } so the UI can drive the
 * countdown. Attach the listener immediately after (see listenToMatchmakingEntry) — the
 * first onSnapshot fires with current state, so a match that lands in the gap isn't missed.
 */
export async function enterMatchmakingQueue({ userID, username, sportId }) {
  const raw = RAW_FROM_ID[sportId];
  if (!raw) throw new Error(`Unknown sport: ${sportId}`);

  const ref = doc(db, "matchmaking_queue", queueDocID(userID, sportId));

  // Delete any stale entry from a previous session (no-op if absent).
  try {
    await deleteDoc(ref);
  } catch (_) {
    /* ignore — nothing to delete */
  }

  const queuedAt = new Date();
  const expiresAt = new Date(queuedAt.getTime() + QUEUE_TTL_MS);

  await setDoc(ref, {
    userID,
    username,
    sport: raw,
    queuedAt,
    expiresAt,
    status: "waiting",
  });

  return { queuedAt, expiresAt };
}

/** Cancels the queue (update status → "cancelled"). */
export async function cancelMatchmaking({ userID, sportId }) {
  await updateDoc(doc(db, "matchmaking_queue", queueDocID(userID, sportId)), {
    status: "cancelled",
  });
}

/** Deletes a queue entry (cleanup after match/expiry). */
export async function deleteMatchmakingEntry({ userID, sportId }) {
  await deleteDoc(doc(db, "matchmaking_queue", queueDocID(userID, sportId)));
}

// ─── Queue listener ───────────────────────────────────────────────────────────

function decodeEntry(snap) {
  if (!snap || !snap.exists()) return null;
  const d = snap.data();
  if (typeof d.userID !== "string" || typeof d.status !== "string") return null;
  return {
    userID: d.userID,
    username: d.username,
    sport: ID_FROM_RAW[d.sport] || null,
    status: d.status, // "waiting" | "matched" | "expired" | "cancelled"
    matchedGameID: typeof d.matchedGameID === "string" ? d.matchedGameID : null,
    queuedAt: toDate(d.queuedAt),
    expiresAt: toDate(d.expiresAt),
  };
}

/**
 * Listens to the user's queue doc for one sport. Returns the onSnapshot unsubscribe.
 * `onChange(entry|null)` is called on every update; the caller maps:
 *   status "matched" + matchedGameID → load that game
 *   status "expired"                 → show expired state
 */
export function listenToMatchmakingEntry({ userID, sportId }, onChange) {
  return onSnapshot(
    doc(db, "matchmaking_queue", queueDocID(userID, sportId)),
    (snap) => onChange(decodeEntry(snap)),
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("matchmakingService: queue listener error", err);
      }
      onChange(null);
    }
  );
}

// ─── Active games (four queries) ────────────────────────────────────────────
// Mirrors loadActivePickUpGames: live games (status in active|waiting) for either player
// slot, plus recently-completed games (status in complete|forfeited, lastActionAt within
// 7 days) the user hasn't viewed yet. Merged + deduped by game id. All four composite
// indexes are deployed (playerAID|playerBID × status, and the same × lastActionAt).

const ACTIVE_STATUSES = ["active", "waiting"];
const TERMINAL_STATUSES = ["complete", "forfeited"];
const COMPLETED_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

export async function loadActivePickUpGames(userID) {
  const games = collection(db, "pickUpGames");
  const cutoff = Timestamp.fromMillis(Date.now() - COMPLETED_LOOKBACK_MS);

  const [liveA, liveB, doneA, doneB] = await Promise.all([
    getDocs(query(games, where("playerAID", "==", userID), where("status", "in", ACTIVE_STATUSES))),
    getDocs(query(games, where("playerBID", "==", userID), where("status", "in", ACTIVE_STATUSES))),
    getDocs(
      query(
        games,
        where("playerAID", "==", userID),
        where("status", "in", TERMINAL_STATUSES),
        where("lastActionAt", ">", cutoff)
      )
    ),
    getDocs(
      query(
        games,
        where("playerBID", "==", userID),
        where("status", "in", TERMINAL_STATUSES),
        where("lastActionAt", ">", cutoff)
      )
    ),
  ]);

  const byID = new Map();

  // Live games: always included.
  for (const snap of [...liveA.docs, ...liveB.docs]) {
    const g = decodePickUpGame(snap);
    if (g) byID.set(g.id, g);
  }

  // Completed games: only those the user hasn't viewed yet.
  for (const snap of [...doneA.docs, ...doneB.docs]) {
    const g = decodePickUpGame(snap);
    if (g && !g.viewedBy.includes(userID)) byID.set(g.id, g);
  }

  return Array.from(byID.values());
}

/**
 * Real-time version of loadActivePickUpGames. Attaches onSnapshot to the same four queries
 * and calls onChange(games[]) on every update, so the ACTIVE DRAFTS list (and the Battles
 * tab dot) reflect picks the moment they land instead of waiting for a poll. Returns a single
 * unsubscribe that detaches all four listeners — call it on unmount (plan §8).
 *
 * The completed-game lookback cutoff is fixed at attach time, which is fine for a session;
 * the list refreshes naturally whenever the screen is re-entered.
 */
export function listenToActivePickUpGames(userID, onChange) {
  const games = collection(db, "pickUpGames");
  const cutoff = Timestamp.fromMillis(Date.now() - COMPLETED_LOOKBACK_MS);

  const queries = [
    query(games, where("playerAID", "==", userID), where("status", "in", ACTIVE_STATUSES)),
    query(games, where("playerBID", "==", userID), where("status", "in", ACTIVE_STATUSES)),
    query(
      games,
      where("playerAID", "==", userID),
      where("status", "in", TERMINAL_STATUSES),
      where("lastActionAt", ">", cutoff)
    ),
    query(
      games,
      where("playerBID", "==", userID),
      where("status", "in", TERMINAL_STATUSES),
      where("lastActionAt", ">", cutoff)
    ),
  ];

  // Each listener owns its bucket of raw docs; any update re-merges all four.
  const buckets = [[], [], [], []];

  const emit = () => {
    const byID = new Map();
    // Live games (buckets 0,1): always included.
    for (const snap of [...buckets[0], ...buckets[1]]) {
      const g = decodePickUpGame(snap);
      if (g) byID.set(g.id, g);
    }
    // Completed games (buckets 2,3): only those the user hasn't viewed yet.
    for (const snap of [...buckets[2], ...buckets[3]]) {
      const g = decodePickUpGame(snap);
      if (g && !g.viewedBy.includes(userID)) byID.set(g.id, g);
    }
    onChange(Array.from(byID.values()));
  };

  const unsubs = queries.map((q, i) =>
    onSnapshot(
      q,
      (snap) => {
        buckets[i] = snap.docs;
        emit();
      },
      (err) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("matchmakingService: active-games listener error", err);
        }
      }
    )
  );

  return () => unsubs.forEach((u) => u());
}
